import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import WageRateFormDialog from "../components/WageRateFormDialog";
import ModuleContextChip from "../components/ModuleContextChip";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { rowOpenProps } from "../hooks/useRowOpen";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import EntityLink from "../components/EntityLink";
import {
    PAYMENT_FREQ_LABEL,
    WAGE_STATUS_LABEL,
    WORKER_CATEGORY_LABEL,
    formatCurrency,
    formatDate,
    statusBadgeClass,
} from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { CheckCircle, Plus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { usePageAction } from "../contexts/UXChromeContext";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import { Checkbox } from "../components/ui/checkbox";
import useBulkSelection from "../hooks/useBulkSelection";
import BulkActionsBar from "../components/BulkActionsBar";

const ALL = "__all__";

export default function WageRates() {
    const { user } = useAuth();
    const { modules, byId: modulesById } = useModules();
    const scopedId = useScopedModuleId();
    const { moduleCode: pathModuleCode } = useParams();
    const navigate = useNavigate();
    const canEdit = useHasPermission("wage_rates.write", scopedId || null);
    const canApprove = useHasPermission("wage_rates.approve", scopedId || null);
    // Iter52d — Per-employee wage rate assignment available on all modules.
    const hasScopedModule = !!scopedId;

    const [tab, setTab] = useState("current");
    // Iter70 — Shared defaults for filter → URL sync + Clear filters reset.
    const FILTER_DEFAULTS = useMemo(() => ({
        module_id: scopedId || ALL,
        worker_category: ALL,
    }), [scopedId]);
    const [filters, setFilters] = useState(FILTER_DEFAULTS);
    // Iter67 F3.1 — Table search input above the Wage Rates table.
    // Filters visible rows by employee full_name OR module_employee_id
    // (case-insensitive substring). Debounced 150ms to feel snappy on
    // long lists.
    const [searchRaw, setSearchRaw] = useState("");
    const [search, setSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchRaw.trim().toLowerCase()), 150);
        return () => clearTimeout(t);
    }, [searchRaw]);
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    // Iter59.p4 — Prefill the New Wage Rate dialog when arriving via the
    // Staff page's "+ Set rate" link. Query params: prefill_employee,
    // prefill_job_title, prefill_worker_category.
    const [searchParams, setSearchParams] = useSearchParams();
    const [showDeleted, setShowDeleted] = useState(false);

    // Iter70 — Two-way URL sync for module + category + search + tab.
    // Debounced 300ms so rapid typing doesn't spam history entries.
    // Also handles the initial hydration from the URL on mount (replaces
    // the previous ad-hoc reads of `searchParams` inside useState initialisers).
    // Sentinel translation: internal state uses ALL = "__all__"; URL uses
    // the friendly literal "all".
    const _allCodec = {
        toUrl: (v) => (v === ALL ? "" : v),
        fromUrl: (raw) => (raw === "all" || raw === "" ? ALL : raw),
    };
    useFilterUrlSync({
        filters: { ...filters, search: searchRaw, show_deleted: showDeleted, tab },
        defaults: { ...FILTER_DEFAULTS, search: "", show_deleted: false, tab: "current" },
        paramMap: {
            module_id: { param: "module_id", ..._allCodec },
            worker_category: { param: "category", ..._allCodec },
            search: "search",
            show_deleted: "show_deleted",
            tab: "tab",
        },
        hydrate: (patch) => {
            if (patch.module_id !== undefined || patch.worker_category !== undefined) {
                const { module_id, worker_category } = patch;
                setFilters((f) => ({
                    ...f,
                    ...(module_id !== undefined ? { module_id } : {}),
                    ...(worker_category !== undefined ? { worker_category } : {}),
                }));
            }
            if (patch.search !== undefined) setSearchRaw(patch.search);
            if (patch.show_deleted !== undefined) setShowDeleted(!!patch.show_deleted);
            if (patch.tab !== undefined) setTab(patch.tab);
        },
    });

    const clearAllFilters = React.useCallback(() => {
        setFilters(FILTER_DEFAULTS);
        setSearchRaw("");
        setShowDeleted(false);
    }, [FILTER_DEFAULTS]);
    // Iter52d — Employees list for hydrating the EMPLOYEE column and the
    // "Assign to specific employee" dropdown on the New Wage Rate modal.
    // Loaded whenever a specific module is scoped (IC / AP / CN).
    const [icEmployees, setIcEmployees] = useState([]);

    usePageAction(
        canEdit ? { label: "New Wage Rate", run: () => setDialogOpen(true) } : null,
        [canEdit],
    );

    useEffect(() => {
        if (!hasScopedModule) { setIcEmployees([]); return; }
        (async () => {
            try {
                const { data } = await api.get("/employees", { params: { module_id: scopedId, page_size: 200 } });
                setIcEmployees(data.items || []);
            } catch { /* ignore */ }
        })();
    }, [hasScopedModule, scopedId]);
    const icEmpById = useMemo(() => Object.fromEntries(icEmployees.map((e) => [e.id, e])), [icEmployees]);

    // Iter59.p4 — Consume `?prefill_employee=…&prefill_job_title=…&prefill_worker_category=…`
    // query params (set by Staff.jsx "+ Set rate"). We wait for `icEmployees`
    // to load so the WageRateFormDialog can render the assign-to-employee
    // dropdown with the right option already selected.
    useEffect(() => {
        const empId = searchParams.get("prefill_employee");
        if (!empId || !hasScopedModule || icEmployees.length === 0) return;
        const jobTitle = searchParams.get("prefill_job_title") || "";
        const workerCat = searchParams.get("prefill_worker_category") || "casual";
        setEditingRate({
            // No `id` → dialog treats this as a NEW record with prefilled fields.
            employee_id: empId,
            module_id: scopedId,
            job_role: jobTitle,
            worker_category: workerCat,
            payment_frequency: "daily",
            currency: "USD",
            status: "draft",
        });
        setDialogOpen(true);
        // Clean the URL so a page refresh doesn't re-open the dialog.
        const next = new URLSearchParams(searchParams);
        ["prefill_employee", "prefill_job_title", "prefill_worker_category"].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
    }, [hasScopedModule, icEmployees.length, searchParams, scopedId, setSearchParams]);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        if (filters.worker_category !== ALL) params.worker_category = filters.worker_category;
        if (showDeleted) params.include_deleted = true;
        const path = tab === "current" ? "/wage-rates" : "/wage-rates/history";
        try {
            const { data } = await api.get(path, { params });
            setRates(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        load();
    }, [tab, filters.module_id, filters.worker_category, showDeleted]);

    const approve = async (rate) => {
        try {
            await api.post(`/wage-rates/${rate.id}/approve`);
            toast.success("Wage rate approved");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const filtered = useMemo(() => {
        const tabFiltered = tab === "current"
            ? rates.filter((r) => ["approved", "pending_approval", "draft"].includes(r.status))
            : rates;
        // Iter67 F3.1 — Local search over employee name + Module ID.
        // Blanket rows (no employee_id) match on job_role so the search
        // still finds "carpenter"-style role rates.
        if (!search) return tabFiltered;
        return tabFiltered.filter((r) => {
            const role = (r.job_role || "").toLowerCase();
            if (role.includes(search)) return true;
            const emp = icEmpById[r.employee_id];
            if (!emp) return false;
            const name = (emp.full_name || "").toLowerCase();
            const mid = (emp.module_employee_id || "").toLowerCase();
            return name.includes(search) || mid.includes(search);
        });
    }, [rates, tab, search, icEmpById]);

    // Iter56 → Iter56c — bulk selection. Uses the SAME permission gate as the
    // individual row action so dept mgrs with module-scoped grants see the
    // toolbar identically to the per-row Approve button.
    const canBulkWrite = useHasPermission("wage_rates.approve", scopedId || null);
    const canBulkDelete = useHasPermission("wage_rates.delete", scopedId || null);
    const bulk = useBulkSelection(filtered, (r) => r.id);

    return (
        <AppShell title="Wage Rates">
            <WageRateFormDialog
                open={dialogOpen}
                onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingRate(null); }}
                onSaved={() => { load(); setEditingRate(null); }}
                initial={editingRate}
                icEmployees={hasScopedModule ? icEmployees : null}
                isICScope={hasScopedModule}
                defaultModuleId={scopedId}
            />

            <section className="space-y-5" data-testid="wage-rates-page">
                <Tabs value={tab} onValueChange={setTab}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <TabsList>
                            <TabsTrigger value="current" data-testid="wage-tab-current">
                                Current Rates
                            </TabsTrigger>
                            <TabsTrigger value="history" data-testid="wage-tab-history">
                                Full History
                            </TabsTrigger>
                        </TabsList>
                        {canEdit && (
                            <Button
                                size="sm"
                                className="asl-navy-bg text-white"
                                onClick={() => setDialogOpen(true)}
                                data-testid="wage-add-button"
                            >
                                <Plus className="h-4 w-4 mr-1" /> New Wage Rate
                            </Button>
                        )}
                    </div>

                    <div className="asl-card p-4 mt-4 flex flex-wrap items-end gap-3">
                        {/* Iter60.p13 — hide Module dropdown when inside a workspace. */}
                        {scopedId ? (
                            <ModuleContextChip moduleId={scopedId} testid="wage-module-chip" />
                        ) : (
                            <FilterSelect
                                label="Module"
                                value={filters.module_id}
                                onValueChange={(v) => setFilters((f) => ({ ...f, module_id: v }))}
                                items={[
                                    { value: ALL, label: "All modules" },
                                    ...modules.map((m) => ({ value: m.id, label: m.name })),
                                ]}
                                testid="wage-filter-module"
                            />
                        )}
                        <FilterSelect
                            label="Category"
                            value={filters.worker_category}
                            onValueChange={(v) =>
                                setFilters((f) => ({ ...f, worker_category: v }))
                            }
                            items={[
                                { value: ALL, label: "All categories" },
                                ...Object.entries(WORKER_CATEGORY_LABEL).map(([v, l]) => ({
                                    value: v,
                                    label: l,
                                })),
                            ]}
                            testid="wage-filter-category"
                        />
                        {/* Iter67 F3.1 — Search input filters visible rows
                            by employee name OR Module ID (e.g. "557"
                            matches "557G"). Debounced 150ms upstream. */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                Search
                            </label>
                            <Input
                                value={searchRaw}
                                onChange={(e) => setSearchRaw(e.target.value)}
                                placeholder="Search by name or Module ID…"
                                className="h-8 w-56 text-xs"
                                data-testid="wage-search-input"
                            />
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                            {/* Iter70 — Reset every managed filter (module,
                                category, search, show-deleted) to its default
                                and drop the corresponding URL params. */}
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={clearAllFilters}
                                data-testid="wage-clear-filters"
                                title="Reset all filters to their defaults"
                            >
                                Clear filters
                            </Button>
                            <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="wage-show-deleted" />
                        </div>
                    </div>

                    <TabsContent value={tab} forceMount>
                        <div className="asl-card overflow-hidden mt-4">
                            {loading ? (
                                <TableSkeleton rows={5} cols={10} testid="wage-loading" />
                            ) : filtered.length === 0 ? (
                                (() => {
                                    const isFilt = filters.module_id !== ALL || filters.worker_category !== ALL;
                                    return (
                                        <EmptyState
                                            icon={BadgeCheck}
                                            title={isFilt ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No wage rates set up yet"}
                                            description={isFilt ? "Adjust filters or clear them." : "Define hourly, daily, or monthly rates by module + role + worker category."}
                                            ctaLabel={canEdit && !isFilt ? "New Wage Rate" : undefined}
                                            onCta={canEdit && !isFilt ? () => setDialogOpen(true) : undefined}
                                            filtered={isFilt}
                                            onClear={() => setFilters({ module_id: scopedId || ALL, worker_category: ALL })}
                                            testid="wage-empty"
                                        />
                                    );
                                })()
                            ) : (
                                <>
                                {(canBulkWrite || canBulkDelete) && (
                                    <BulkActionsBar
                                        resource="wage-rates"
                                        selectedIds={bulk.selectedIds}
                                        onClear={bulk.clear}
                                        onRefresh={load}
                                        entityLabel="wage rate"
                                        canApprove={canBulkWrite}
                                        approveLabel="Approve"
                                        approveVerb="approved"
                                    />
                                )}
                                <table className="asl-table asl-table--zebra" data-testid="wage-table">
                                    <thead
                                        className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                        style={{
                                            background: "var(--asl-navy-50)",
                                            borderBottom: "1px solid var(--asl-border)",
                                        }}
                                    >
                                        <tr>
                                            {(canBulkWrite || canBulkDelete) && (
                                                <th className="px-3 py-3 w-8">
                                                    <Checkbox
                                                        checked={bulk.allVisibleSelected}
                                                        onCheckedChange={bulk.toggleAll}
                                                        data-testid="bulk-select-all"
                                                    />
                                                </th>
                                            )}
                                            <th className="text-left px-4 py-3">Module</th>
                                            <th className="text-left px-4 py-3">Job Role</th>
                                            {hasScopedModule && <th className="text-left px-4 py-3" data-testid="wage-th-employee">Employee</th>}
                                            <th className="text-left px-4 py-3">Category</th>
                                            <th className="text-left px-4 py-3">Hourly</th>
                                            <th className="text-left px-4 py-3">Daily</th>
                                            <th className="text-left px-4 py-3">Monthly</th>
                                            <th className="text-left px-4 py-3">Frequency</th>
                                            <th className="text-left px-4 py-3">Effective</th>
                                            <th className="text-left px-4 py-3">Status</th>
                                            <th className="text-right px-4 py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((r) => (
                                            <tr
                                                key={r.id}
                                                {...rowOpenProps(navigate, () => { setEditingRate(r); setDialogOpen(true); }, { testid: `wage-row-${r.id}`, className: "border-t" })}
                                                style={{ borderColor: "var(--asl-border)" }}
                                            >
                                                {(canBulkWrite || canBulkDelete) && (
                                                    <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={bulk.isSelected(r.id)}
                                                            onCheckedChange={() => bulk.toggle(r.id)}
                                                            data-testid={`bulk-select-${r.id}`}
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-4 py-3 text-xs">{modulesById[r.module_id]?.name || "—"}</td>
                                                <td className="px-4 py-3 font-medium asl-navy-text">{r.job_role}</td>
                                                {hasScopedModule && (
                                                    <td className="px-4 py-3 text-xs" data-testid={`wage-emp-${r.id}`}>
                                                        {r.employee_id ? (() => {
                                                            const emp = icEmpById[r.employee_id];
                                                            const idLabel = emp?.module_employee_id || "";
                                                            return (
                                                                <span className="inline-flex items-center gap-1.5">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Person-specific rate" />
                                                                    <EntityLink to={emp ? `/m/${(pathModuleCode || "IC").toLowerCase()}/staff/${r.employee_id}` : null}
                                                                                className="asl-navy-text font-medium hover:underline">
                                                                        {emp?.full_name || "—"}
                                                                    </EntityLink>
                                                                    {idLabel && (
                                                                        <span className="text-[10px] text-[color:var(--asl-muted)] mono">· {idLabel}</span>
                                                                    )}
                                                                </span>
                                                            );
                                                        })() : (
                                                            <span className="inline-flex items-center gap-2 flex-wrap">
                                                                <span className="text-[color:var(--asl-muted)] italic">— All matching employees —</span>
                                                                {/* Iter59.p4 — subtle pill showing how many active
                                                                    employees in this module inherit this rate. */}
                                                                {typeof r.matching_employees_count === "number" && (
                                                                    <span
                                                                        className="text-[10px] px-1.5 py-0.5 rounded-full border bg-[color:var(--asl-navy-50)] text-[color:var(--asl-navy)]"
                                                                        style={{ borderColor: "var(--asl-border)" }}
                                                                        data-testid={`wage-role-count-${r.id}`}
                                                                        title="Number of active employees in this module whose job title + worker category match this rate">
                                                                        Job-role · {r.matching_employees_count} emp
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-4 py-3 text-xs">{WORKER_CATEGORY_LABEL[r.worker_category]}</td>
                                                <td className="px-4 py-3 mono text-xs">{r.hourly_rate ? formatCurrency(r.hourly_rate) : "—"}</td>
                                                <td className="px-4 py-3 mono text-xs">{r.daily_rate ? formatCurrency(r.daily_rate) : "—"}</td>
                                                <td className="px-4 py-3 mono text-xs">{r.monthly_rate ? formatCurrency(r.monthly_rate) : "—"}</td>
                                                <td className="px-4 py-3 text-xs">{PAYMENT_FREQ_LABEL[r.payment_frequency]}</td>
                                                <td className="px-4 py-3 mono text-xs">{formatDate(r.effective_date)}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(r.status)}`}>
                                                        {WAGE_STATUS_LABEL[r.status]}
                                                    </span>
                                                    {r.deleted_at && <DeletedPill deletedAt={r.deleted_at} />}
                                                </td>
                                                <td className="px-4 py-3 text-right" data-no-row-click>
                                                    <div className="flex items-center justify-end gap-1">
                                                        {r.status === "pending_approval" && canApprove && !r.deleted_at && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => approve(r)}
                                                                data-testid={`wage-approve-${r.id}`}
                                                            >
                                                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                                                            </Button>
                                                        )}
                                                        <RowActions
                                                            entity="wage_rates"
                                                            endpoint="/wage-rates"
                                                            id={r.id}
                                                            label={r.job_role}
                                                            deleted={!!r.deleted_at}
                                                            moduleId={r.module_id}
                                                            hardDeleteSupported={false}
                                                            onChange={load}
                                                            testid={`wage-actions-${r.id}`}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </section>
        </AppShell>
    );
}

function FilterSelect({ label, value, onValueChange, items, testid }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <Select value={value} onValueChange={onValueChange}>
                <SelectTrigger className="w-52" data-testid={testid}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {items.map((i) => (
                        <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                            {i.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
